// Host-based application context. ONE codebase, ONE backend — only the mounted page routes differ.
//   ponesi.rs / www.ponesi.rs -> customer
//   partner.ponesi.rs         -> restaurant panel
//   admin.ponesi.rs           -> platform admin
// SECURITY: this decides only WHICH UI is rendered. It NEVER grants authentication, restaurant
// membership, a role or PLATFORM_ADMIN — every privileged request is still authorized server-side.
export const CUSTOMER_HOSTS = ["ponesi.rs", "www.ponesi.rs"];
export const PARTNER_HOSTS = ["partner.ponesi.rs"];
export const ADMIN_HOSTS = ["admin.ponesi.rs"];

const host = () => (typeof window === "undefined" ? "" : window.location.hostname.toLowerCase());

// An approved Emergent PREVIEW/dev hostname only. `*.emergent.host` is deliberately NOT included:
// a live deployment is served there before a custom domain is attached, so honouring ?ctx= on it
// would expose a privileged UI shell on a production URL.
export function isPreviewHost(h = host()) {
  if (CUSTOMER_HOSTS.includes(h) || PARTNER_HOSTS.includes(h) || ADMIN_HOSTS.includes(h)) return false;
  return h === "localhost" || h === "127.0.0.1" || h.endsWith(".preview.emergentagent.com");
}

// Preview-only context switch. Needs an approved preview hostname AND the build flag explicitly on.
// Production sets REACT_APP_ENABLE_PREVIEW_CONTEXT_SWITCH=false, and the hostname check alone
// already blocks it on ponesi.rs / partner.ponesi.rs / admin.ponesi.rs.
function previewSwitchEnabled() {
  return process.env.REACT_APP_ENABLE_PREVIEW_CONTEXT_SWITCH === "true" && isPreviewHost();
}

const CTX_KEY = "ponesi_preview_ctx";

function previewCtx() {
  if (!previewSwitchEnabled()) return null;
  const q = new URLSearchParams(window.location.search).get("ctx");
  if (["customer", "partner", "admin", "all"].includes(q)) {
    sessionStorage.setItem(CTX_KEY, q);      // survives in-app navigation during a preview test
    return q;
  }
  const saved = sessionStorage.getItem(CTX_KEY);
  return ["customer", "partner", "admin", "all"].includes(saved) ? saved : null;
}

/** "customer" | "partner" | "admin" | "all" ("all" only on preview with no ctx chosen). */
export function appContext() {
  const h = host();
  if (ADMIN_HOSTS.includes(h)) return "admin";
  if (PARTNER_HOSTS.includes(h)) return "partner";
  if (CUSTOMER_HOSTS.includes(h)) return "customer";
  return previewCtx() || (isPreviewHost(h) ? "all" : "customer"); // unknown prod host => customer only
}

export const CTX = appContext();
export const isCustomerCtx = CTX === "customer" || CTX === "all";
export const isPartnerCtx = CTX === "partner" || CTX === "all";
export const isAdminCtx = CTX === "admin" || CTX === "all";

/** Where each context sends a user after logout — never the customer homepage for panels. */
export const logoutPath = () => (CTX === "admin" ? "/admin" : CTX === "partner" ? "/restaurant" : "/");
